import { getApiBase } from '../apiBase';
import { getTenantHeaders } from '../api';
// src/calendar/calendarApi.js
const BASE = getApiBase();
const API = {
  customers: `${BASE}/api/customers`,
  employees: `${BASE}/api/settings/employees`,
  hours: `${BASE}/api/settings/hours`,
  jobtypes: `${BASE}/api/settings/appointment-types`, // may not exist yet
  business: `${BASE}/api/settings/business`,
  appointments: `${BASE}/api/appointments`,
};

export const safeJson = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

// --------------------
// Customers
// --------------------
export const fetchCustomers = async () => {
  const res = await fetch(API.customers, {
    headers: { ...getTenantHeaders() },
  });
  if (!res.ok) throw new Error(`customers ${res.status}`);
  const data = await safeJson(res);
  return Array.isArray(data) ? data : [];
};

export const fetchCustomerAppointments = async ({ customerId, scope, cursor, limit = 25, signal }) => {
  const qs = new URLSearchParams({ scope, limit: String(limit) });
  if (cursor) qs.set("cursor", cursor);
  const res = await fetch(
    `${API.customers}/${encodeURIComponent(String(customerId))}/appointments?${qs.toString()}`,
    {
      headers: { ...getTenantHeaders() },
      signal,
    }
  );
  const data = await safeJson(res);
  if (!res.ok) throw new Error(data?.error || `customer appointments ${res.status}`);
  return {
    appointments: Array.isArray(data?.appointments) ? data.appointments : [],
    hasMore: data?.hasMore === true,
    nextCursor: typeof data?.nextCursor === "string" ? data.nextCursor : null,
  };
};

// IMPORTANT: your backend expects fields like:
// name, contactPerson, billingEmail, phone, office_phone, mobile_phone, street, city, state, zip, terms, notes, address
export const createCustomer = async (qc) => {
  const name = String(qc?.company || qc?.name || "").trim();
  const contactPerson = String(qc?.contact || qc?.contactPerson || "").trim();

  // build a human "address" too (your DB shows an address field in many rows)
  const street = String(qc?.street || "").trim();
  const city = String(qc?.city || "").trim();
  const state = String(qc?.state || "").trim();
  const zip = String(qc?.zip || "").trim();
  const address = [street, [city, state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");

  const payload = {
    name,
    contactPerson,
    billingEmail: String(qc?.email || qc?.billingEmail || "").trim(),
    phone: String(qc?.office_phone || qc?.phone || "").trim(),
    office_phone: String(qc?.office_phone || "").trim(),
    mobile_phone: String(qc?.mobile_phone || "").trim(),
    street,
    city,
    state,
    zip,
    address,
    terms: String(qc?.terms || "COD").trim(),
    notes: String(qc?.notes || "").trim(),
  };

  const res = await fetch(API.customers, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getTenantHeaders() },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`create customer ${res.status} ${txt}`);
  }

  return await safeJson(res);
};

// --------------------
// Employees / Hours / Job Types
// --------------------
export const fetchEmployees = async () => {
  const res = await fetch(API.employees, {
    headers: { ...getTenantHeaders() },
  });
  if (!res.ok) throw new Error(`employees ${res.status}`);
  const data = await safeJson(res);
  return Array.isArray(data) ? data : [];
};

export const fetchHours = async () => {
  const res = await fetch(API.hours, {
    headers: { ...getTenantHeaders() },
  });
  if (!res.ok) throw new Error(`hours ${res.status}`);
  return (await safeJson(res)) || null;
};

// Job types endpoint is missing right now — return [] instead of throwing
export const fetchJobTypes = async () => {
  const res = await fetch(API.jobtypes, {
    headers: { ...getTenantHeaders() },
  });
  if (!res.ok) return [];
  const data = await safeJson(res);
  return Array.isArray(data) ? data : [];
};

export const fetchBusinessSettings = async () => {
  const res = await fetch(API.business, {
    headers: { ...getTenantHeaders() },
  });
  if (!res.ok) throw new Error(`business settings ${res.status}`);
  return (await safeJson(res)) || {};
};

// --------------------
// Appointments
// --------------------
export const fetchAppointments = async ({ start, end, signal }) => {
  const qs = new URLSearchParams({ start, end }).toString();
  const res = await fetch(`${API.appointments}?${qs}`, {
    headers: { ...getTenantHeaders() },
    signal,
  });
  if (!res.ok) throw new Error(`appointments ${res.status}`);
  const data = await safeJson(res);
  return Array.isArray(data) ? data : [];
};

export const createAppointment = async (payload) => {
  const res = await fetch(API.appointments, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getTenantHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`create appt ${res.status} ${txt}`);
  }
  return await safeJson(res);
};

export const updateAppointment = async (id, payload) => {
  const res = await fetch(`${API.appointments}/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getTenantHeaders() },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`update appt ${res.status} ${txt}`);
  }
  return await safeJson(res);
};

export const deleteAppointmentApi = async (id) => {
  const res = await fetch(`${API.appointments}/${id}`, {
    method: "DELETE",
    headers: { ...getTenantHeaders() },
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`delete appt ${res.status} ${txt}`);
  }
  return true;
};
